import { HandbagIcon, StackIcon } from "@phosphor-icons/react/dist/ssr";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { truncate } from "@/core/shared/v2/common/helpers/string";
import { toBase64 } from "@/core/shared/v2/common/helpers/to-base64";
import { cn } from "@/core/shared/v2/lib/utils";
import { Badge } from "@/core/shared/v2/ui/badge";
import { Button } from "@/core/shared/v2/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/core/shared/v2/ui/dialog";
import { LazyImage } from "@/core/shared/v2/ui/lazy-image";
import { shimmer } from "@/core/shared/v2/ui/shimmer";
import { formatPrice } from "@/core/utils/format-price";
import type { ItemListId } from "../../../tracking/create-ecommerce-event";
import type { BloodTestPackage } from "../../models/blood-test-package";
import { safeTrackEvent, viewItemEvent } from "../../tracking/events";

interface CatalogItemProps {
  voucher: BloodTestPackage;
  showActions?: boolean;
  isSelected?: boolean;
  quantity?: number;
  simplified?: boolean;
  onAddToCart?: (e: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => void;
  itemListId?: ItemListId;
  index?: number;
}

export function CatalogItem({
  voucher,
  showActions = true,
  isSelected = false,
  onAddToCart,
  quantity,
  simplified = false,
  itemListId = "listing",
  index,
}: CatalogItemProps) {
  const [isReadMoreModalOpen, setIsReadMoreModalOpen] = useState(false);
  const { t } = useTranslation();

  const handleAddToCart = (e: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => {
    e.stopPropagation();
    onAddToCart?.(e);
  };

  const handleOpenReadMoreModal = () => {
    setIsReadMoreModalOpen(true);
    safeTrackEvent(
      () =>
        viewItemEvent(
          [
            {
              ...voucher,
              productType: voucher.productType ?? "blood-test-package",
              quantity: 1,
              item_list_id: itemListId,
              index,
            },
          ],
          {
            value: Number(voucher.grossPrice),
          },
        ),
      "view_item",
      "catalog-item handleOpenReadMoreModal",
    );
  };

  const imageSize = simplified ? 100 : 176;

  return (
    <>
      {/* biome-ignore lint/a11y/useSemanticElements: div used to avoid invalid nested button (Read more + Add to cart inside card) */}
      <div
        role="button"
        tabIndex={0}
        data-testid="catalog-item"
        className={cn([
          "flex cursor-pointer flex-col gap-4 rounded-2xl border-2 bg-background p-3 shadow-sm lg:gap-3 lg:p-4",
          { "border-dodger-blue-700": isSelected },
        ])}
        onClick={handleOpenReadMoreModal}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            handleOpenReadMoreModal();
          }
        }}
      >
        <div className="flex flex-1 gap-4 lg:gap-5">
          <div
            className={cn("shrink-0", {
              "h-20 w-20 lg:h-[176px] lg:w-[176px]": !simplified,
              "h-20 w-20 lg:h-[100px] lg:w-[100px]": simplified,
            })}
          >
            {voucher.imageUrl ? (
              <LazyImage
                src={voucher.imageUrl}
                alt={voucher.name}
                width={imageSize}
                height={imageSize}
                className="h-full rounded-lg max-lg:size-20"
                placeholder={`data:image/svg+xml;base64,${toBase64(shimmer(imageSize, imageSize))}`}
              />
            ) : (
              <div
                className={cn("rounded-lg bg-gray-100 max-lg:size-20", {
                  "h-[176px] w-[176px]": !simplified,
                  "h-[100px] w-[100px]": simplified,
                })}
              ></div>
            )}
          </div>
          <div className="flex flex-1 flex-col gap-2">
            <div className="flex flex-1 flex-col gap-3 lg:gap-1">
              <div className="font-semibold text-sm lg:text-xl">{voucher.name}</div>
              {voucher.bloodTests && <ProductsInPackage productsInPackage={voucher.bloodTests} />}
              {!simplified && (
                <p className="mb-3 hidden text-grey-400 text-sm lg:block">
                  {truncate(voucher.description, { length: 128 })}{" "}
                  {voucher.description.length > 128 && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleOpenReadMoreModal();
                      }}
                      className="ml-1 text-foreground hover:underline"
                    >
                      {t("COMMONS.GENERAL.READ_MORE")}
                    </button>
                  )}
                </p>
              )}
            </div>
            <PriceFooter
              showActions={showActions}
              voucher={voucher}
              onAddToCart={handleAddToCart}
              isSelected={isSelected}
              quantity={quantity}
              className="pt-1"
            />
          </div>
        </div>
      </div>

      <Dialog open={isReadMoreModalOpen} onOpenChange={setIsReadMoreModalOpen}>
        <DialogContent className="max-lg: max-w-[900px]">
          <DialogHeader className="flex items-center p-4">
            <DialogTitle className="font-bold text-lg">{voucher.name}</DialogTitle>
          </DialogHeader>
          <div className="p-3">
            <h3 className="mb-4 font-bold text-lg">{t("VOUCHERS_DISTRIBUTION.VOUCHERS_LIST.DESCRIPTION")}:</h3>
            <p
              className="whitespace-pre-line pb-4 [&_a]:text-dodger-blue-700 [&_a]:underline"
              dangerouslySetInnerHTML={{ __html: voucher.description }}
            />
            <h3 className="mb-4 font-bold text-lg">{t("VOUCHERS_DISTRIBUTION.VOUCHERS_LIST.INCLUDED_PRODUCTS")}:</h3>
            <div className="divide-y divide-grey-200">
              <ul className="flex flex-wrap gap-2">
                {voucher.bloodTests
                  .sort((a, b) => {
                    const aLength = a.name.length;
                    const bLength = b.name.length;

                    if (aLength <= bLength) return -1;
                    if (bLength <= aLength) return 1;

                    return 0;
                  })

                  .filter(Boolean)
                  .map(({ name }) => (
                    <li
                      key={name}
                      className="inline-block rounded-full bg-[#F5F5F4] px-3 py-1 text-foreground text-sm max-lg:mr-1 lg:px-4 lg:py-1.5 lg:text-base"
                    >
                      <span>{truncate(name, { length: 42 })}</span>
                    </li>
                  ))}
              </ul>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

interface PriceFooterProps {
  voucher: BloodTestPackage;
  onAddToCart: (e: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => void;
  className?: string;
  showActions?: boolean;
  isSelected?: boolean;
  quantity?: number;
}

function PriceFooter({ voucher, onAddToCart, className, isSelected, showActions = true, quantity }: PriceFooterProps) {
  const { t } = useTranslation();
  const limitExhausted = voucher.limitAvailable === false;

  const handleClick = (e: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => {
    e.stopPropagation(); // Ensure "Add to cart" doesn't count as clicking the product card link
    onAddToCart(e);
  };

  return (
    <div className={cn("flex items-center justify-end gap-3", className)}>
      <div className="text-right">
        {limitExhausted && showActions ? (
          <p className="text-gray-500 lg:text-xl">{t("VOUCHERS_DISTRIBUTION.VOUCHERS_LIST.LIMIT_EXHAUSTED")}</p>
        ) : (
          <p className="font-semibold lg:text-2xl">
            {Number(voucher.grossPrice) > 0
              ? formatPrice(voucher.grossPrice)
              : t("VOUCHERS_DISTRIBUTION.VOUCHERS_LIST.FOR_FREE")}
          </p>
        )}
        {limitExhausted && showActions
          ? null
          : voucher.regularPrice &&
            Number(voucher.regularPrice) > Number(voucher.grossPrice) && (
              <p className="text-muted-foreground text-xs">
                {t("VOUCHERS_DISTRIBUTION.VOUCHERS_LIST.REGULAR_PRICE")}: {formatPrice(voucher.regularPrice)}
              </p>
            )}
      </div>
      {showActions && !limitExhausted && (
        <>
          <Button
            arrowOnHover={false}
            size="lg"
            onClick={handleClick}
            className={cn("max-lg:hidden", { "bg-dodger-blue-700": isSelected })}
            aria-label={t("VOUCHERS_DISTRIBUTION.VOUCHERS_LIST.PICK")}
            rightSection={<HandbagIcon size={16} />}
            data-testid="add-to-cart-button"
          >
            {isSelected && quantity && quantity > 1 ? (
              <div className="flex items-center gap-2">
                <Badge
                  key={quantity}
                  className="zoom-in-50 flex h-5 w-5 shrink-0 animate-in items-center justify-center rounded-full bg-white p-0 text-dodger-blue-700 duration-200"
                >
                  <span className="font-bold text-xs leading-none">{quantity}</span>
                </Badge>
                <span>{t("VOUCHERS_DISTRIBUTION.VOUCHERS_LIST.IN_CART")}</span>
              </div>
            ) : isSelected ? (
              t("VOUCHERS_DISTRIBUTION.VOUCHERS_LIST.IN_CART")
            ) : (
              t("VOUCHERS_DISTRIBUTION.VOUCHERS_LIST.PICK")
            )}
          </Button>
          <Button
            arrowOnHover={false}
            size="icon"
            onClick={handleClick}
            className="min-h-12 min-w-12 lg:hidden"
            aria-label={t("VOUCHERS_DISTRIBUTION.VOUCHERS_LIST.PICK")}
            data-testid="add-to-cart-button-mobile"
          >
            <HandbagIcon className="size-6" alt={t("VOUCHERS_DISTRIBUTION.VOUCHERS_LIST.PICK")} />
          </Button>
        </>
      )}
    </div>
  );
}

interface ProductsInPackageProps {
  productsInPackage: BloodTestPackage["bloodTests"];
}

export function ProductsInPackage({ productsInPackage }: ProductsInPackageProps) {
  const { t } = useTranslation();
  const total = productsInPackage.length;

  return (
    <div className="flex-col gap-2 lg:flex">
      <p className="inline-flex items-center text-sm max-lg:font-semibold">
        <StackIcon size={14} />
        &nbsp;
        <span>{t("VOUCHERS_DISTRIBUTION.VOUCHERS_LIST.BLOOD_TESTS_COUNT", { count: total })}</span>
        &nbsp;
      </p>
    </div>
  );
}
